import { useState, FunctionComponent } from 'react';

import Collection from 'components/collection/Collection';
import { useNavigate } from 'react-router-dom';

import { Divider, TablePagination, CardContent, Card, Stack, Typography,
  Menu, MenuItem, TextField, InputAdornment, Button, Tooltip
 } from '@mui/material';

import {SEARCH, PLUS, REMINDER} from 'assets/Icons'
import { Toolbar } from 'components/common/Toolbar';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';

import { CollectionModel } from 'model/collection/CollectionModels';
import usePlan from 'model/auth/PlanStore';
import SortIcon from '@mui/icons-material/Sort';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Pagination as MuiPagination, styled } from "@mui/material";

const StyledPagination = styled(MuiPagination)(({ theme }) => ({
  "& .MuiPaginationItem-root": {
    [theme.breakpoints.down("sm")]: {
      height: "1.8rem",
      width: "1.8rem"
    }
  }
}));

export class CollectionListProps {
  collections: CollectionModel[];
}

export const CollectionList: FunctionComponent<CollectionListProps> = ({collections}) => {
  const [filter, setFilter] = useState<string>(""); // Search input
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState("name");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("asc");  
  const [sortByMenuAnchorEl, setSortByMenuAnchorEl] = useState(null);  
  const navigate = useNavigate()
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { collectionsLimit } = usePlan();
  const atLimit = collectionsLimit !== null && collections.length >= collectionsLimit;

  const sortByMenuOpen = Boolean(sortByMenuAnchorEl);

  const handleSortByClick = (event: any) => {
      setSortByMenuAnchorEl(event.currentTarget);
  };

  const handleSortByMenuClose = (sellection: string) => {
      if (sellection === "ignore") {
        setSortByMenuAnchorEl(null);
        return;
      }
      setSortOrder(sortBy === sellection && sortOrder === "asc" ? "desc" : "asc");
      setSortBy(sellection);
      setSortByMenuAnchorEl(null);
  };

  const handleEdit = (collection: any) => {
    navigate(`/collection?id=${collection.id}`)
  };
    
  const handleNew = () => {
    navigate(`/collection`);
  };

  // Handle search input
  const handleFilterChange = (event: any) => {
      setFilter(event.target.value.toLowerCase());
      setPage(0);
  };

  const handlePageChange = (_: any, newPage: any) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (_: any) => {    
    setRowsPerPage(_.target.value);
    setPage(0);  // Reset to first page when changing rows per page
  };
  
  // Filter the data
  let filteredData = collections.filter(
      (row: CollectionModel) =>
          row.recipient.toLowerCase().includes(filter) ||
          row.title.toLowerCase().includes(filter) ||
          row.amount.toString().includes(filter)
  );
  
  // Sort the data
  filteredData.sort((a: CollectionModel, b: CollectionModel) => {
      if (a[sortBy as keyof CollectionModel] < b[sortBy as keyof CollectionModel]) return sortOrder === "asc" ? -1 : 1;
      if (a[sortBy as keyof CollectionModel] > b[sortBy as keyof CollectionModel]) return sortOrder === "asc" ? 1 : -1;
      return 0;
  });

  const toolbarItemsDesktop = [
    <Button variant='contained' key="1" color="inherit" onClick={handleSortByClick}><SortIcon aria-hidden="true" />&nbsp;Sort</Button>,
    <Tooltip key="2" title={atLimit ? "Please contact us to create more collections" : ""}><span style={{display: "contents"}}>
      <Button variant='contained' color="inherit" disabled={atLimit} onClick={handleNew}>{PLUS(20)}&nbsp;New</Button>
    </span></Tooltip>,
    <TextField key="3" style={{maxWidth: "210px"}} size="small" onChange={handleFilterChange}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            {SEARCH}
          </InputAdornment>
        ), 
      }}
    />
  ]

  const toolbarItemsMobile = [
    <Button aria-label="Sort" variant='contained' key="1" color="inherit" sx={{maxWidth: "50px !important", minWidth: "50px !important", width: "50px !important"}} onClick={handleSortByClick}>
      <SortIcon aria-hidden="true" />
    </Button>,
    <Tooltip key="2" title={atLimit ? "Please contact us to create more collections" : ""} enterTouchDelay={0} leaveTouchDelay={3000}><span style={{display: "contents"}}>
      <Button aria-label="New collection" variant='contained' color="inherit" disabled={atLimit} sx={{maxWidth: "50px !important", minWidth: "50px !important", width: "50px !important"}} onClick={handleNew}>{PLUS(20)}</Button>
    </span></Tooltip>,
    <TextField key="3" style={{width: "210px"}} size="small" onChange={handleFilterChange}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            {SEARCH}
          </InputAdornment>
        ), 
      }}
    />
  ]
  
  return (<>
    <Stack spacing={1}>
      <Toolbar items={isMobile? toolbarItemsMobile : toolbarItemsDesktop} 
              icon={REMINDER(20, {marginBottom: "-2px", marginRight: "10px"})}
              title='COLLECTIONS'/>

      <Card variant="outlined">
        <CardContent>
          <Stack>
            {Object.entries(filteredData).length === 0 ? 
                <Typography className='wizard-subtitle-color' variant="body1" gutterBottom 
                  sx={{ wordBreak: "break-word", textAlign: "center", marginTop: "12px" }} marginBottom={1}>
                  No records found.
                </Typography> : 
                filteredData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((collection: CollectionModel, index: any) => (
                    <div key={collection.id + index}>                      
                    <Collection key={collection.id}
                                collection={collection}
                                onClick={(c: any) => handleEdit(collection)}/>
                      <Divider sx={{mb: 1, mt: 1}}/>
                    </div>
                  ))}
          </Stack>
        </CardContent>
      </Card>
      <Card variant='outlined'>
        <CardContent style={{marginBottom: "0", padding: "0"}}>
          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handlePageChange}
            rowsPerPageOptions={[10, 25, 50]}
            onRowsPerPageChange={handleRowsPerPageChange}
          />
        </CardContent>
      </Card>
    </Stack>

    <Menu anchorEl={sortByMenuAnchorEl} open={sortByMenuOpen} onClose={()=>handleSortByMenuClose("ignore")}>
      <MenuItem onClick={() => handleSortByMenuClose("daysUntilNext")}>
        Upcoming Date&nbsp;{sortBy === "daysUntilNext"?sortOrder === "asc"?<KeyboardArrowUpIcon/>:<KeyboardArrowDownIcon/>:<></>}
      </MenuItem>
      <MenuItem onClick={() => handleSortByMenuClose("recipient")}>
        Recipient&nbsp;{sortBy === "recipient"?sortOrder === "asc"?<KeyboardArrowUpIcon/>:<KeyboardArrowDownIcon/>:<></>}
      </MenuItem>
      <MenuItem onClick={() => handleSortByMenuClose("title")}>
        Title&nbsp;{sortBy === "title"?sortOrder === "asc"?<KeyboardArrowUpIcon/>:<KeyboardArrowDownIcon/>:<></>}
      </MenuItem>
      <MenuItem onClick={() => handleSortByMenuClose("amount")}>
        Amount&nbsp;{sortBy === "amount"?sortOrder === "asc"?<KeyboardArrowUpIcon/>:<KeyboardArrowDownIcon/>:<></>}
      </MenuItem>
      <MenuItem onClick={() => handleSortByMenuClose("endDate")}>
        End Date&nbsp;{sortBy === "endDate"?sortOrder === "asc"?<KeyboardArrowUpIcon/>:<KeyboardArrowDownIcon/>:<></>}
      </MenuItem>
      {!sortBy?<></>:<MenuItem onClick={() => handleSortByMenuClose(null)}>
        (None)
      </MenuItem>}
    </Menu></>);
}
export default CollectionList;